<template>
  <div id="datasetList">
    <div class="mb-3" v-if="$store.state.dataset.dynamic">
      <!-- Primary Actions -->
      <div class="d-flex align-items-center mb-2">
        <b-button
          @click="selectFromMap()"
          variant="success"
          class="mr-2"
          v-if="$store.state.drawer.datasetDetails.position === 'expand'"
        >
          Select on Map
        </b-button>
        <b-button
          @click="exitSelectionModeAndExpandDrawer"
          variant="success"
          class="mr-2"
          v-else
        >
          Full Screen Grid
        </b-button>
        
        <!-- Selection Tools -->
        <b-button 
          @click="selectAllDatapoints" 
          variant="primary" 
          class="mr-2"
          v-if="datapointsData.length > 0"
        >
          Select All ({{ datapointsData.length }})
        </b-button>
        <b-button 
          @click="clearAllSelections" 
          variant="secondary"
          class="mr-2"
          v-if="selectedDatapoints.length > 0"
        >
          Clear All
        </b-button>
        
        <!-- Selection Counter -->
        <small class="text-muted" v-if="selectedDatapoints.length > 0">
          {{ selectedDatapoints.length }} selected
        </small>
      </div>
      
      <!-- Bulk Actions (only show when items are selected) -->
      <div v-if="selectedDatapoints.length > 0">
        <b-button @click="showBulkUpdateModal = true" variant="warning" class="mr-2">
          Bulk Update
        </b-button>
        <b-dropdown variant="info" class="mr-2" text="Download Selected">
          <template #button-content>
            <i class="far fa-file-download"></i> Download Selected
          </template>
          <b-dropdown-item @click="downloadSelectedCSV()">CSV</b-dropdown-item>
          <b-dropdown-item @click="downloadSelectedGeoJSON()">GeoJSON</b-dropdown-item>
        </b-dropdown>
        <b-button @click="confirmDelete = true" variant="danger">
          Delete Selected
        </b-button>
      </div>
    </div>
    <vue-good-table
      v-if="!hideTable"
      :rows="datapointsData"
      :columns="tableColumns"
      :select-options="selectOptions"
      :pagination-options="paginationOptions"
      @on-selected-rows-change="onSelectionChange"
      @on-cell-click="handleCellClick"
      ref="datapointsTable"
    />
    <BulkUpdateModal
      v-if="showBulkUpdateModal"
      :selectedDatapoints="selectedDatapoints"
      @closeModal="showBulkUpdateModal = false"
      @bulkUpdate="bulkUpdateDatapoints"
    />
    <div
      class="overlay"
      style="padding-top:200px;"
      v-if="processingDatapoints"
    >
      <p class="lead text-light text-center"><strong>Updating Datapoints</strong></p>
      <b-progress
        class="ml-5 mr-5"
        :value="datapointProcessing"
        :max="numberOfDatapoints"
        show-progress
        animated
      ></b-progress>
    </div>
    <Confirm
      v-if="confirmDelete"
      text="WARNING: You are about to delete all of these selected datapoints. Are you sure you want to do that? This cannot be undone!!"
      v-on:confirmed="deleteSelectedDatapoints($event)"
    />
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import 'vue-good-table/dist/vue-good-table.css'
import { format } from 'date-fns'
import Confirm from '../Confirm'
import BulkUpdateModal from './BulkUpdateModal.vue'
import firebase from 'firebase/compat/app'
import moment from 'moment'
import Papa from 'papaparse'
const fb = require('../../firebaseConfig.js')
const turf = require('@turf/turf')

export default {
  data () {
    return {
      hideTable: false,
      confirmDelete: false,
      showBulkUpdateModal: false,
      numberOfDatapoints: 0,
      datapointProcessing: 0,
      processingDatapoints: false,
      selectedDatapointsGeoJson: {
        type: 'FeatureCollection',
        features: []
      },
      paginationOptions: {
        enabled: true,
        perPage: 500,
        perPageDropdown: [10, 25, 50, 100, 500],
        dropdownAllowAll: false,
        nextLabel: 'Next',
        prevLabel: 'Previous',
        rowsPerPageLabel: 'Rows per page',
        ofLabel: 'of',
        pageLabel: 'page',
        allLabel: 'All'
      }
    }
  },
  components: {
    VueGoodTable,
    BulkUpdateModal,
    Confirm
  },
  created () {
    this.$store.commit('expandDrawer', 'datasetDetails')
    // Clear any existing selections when component loads
    this.clearAllSelections()
    // Add escape key listener to exit selection mode
    document.addEventListener('keydown', this.handleKeydown)
  },
  destroyed () {
    this.$store.commit('shrinkDrawer', 'datasetDetails')
    // Clear selections when navigating away from component
    this.clearAllSelections()
    // Exit selection mode when component is destroyed
    this.exitSelectionMode()
    // Remove escape key listener
    document.removeEventListener('keydown', this.handleKeydown)
  },
  computed: {
    selectedDatapoints: {
      get () {
        return this.$store.state.selectedDatapoints || []
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'selectedDatapoints', value: value })
      }
    },
    datapointsData () {
      return this.$store.state.datasetGeoJson.features
    },
    tableColumns () {
      const collectorFields = this.$store.state.collector.fields
      const columns = []
      
      // Only show ID column for dynamic datasets
      if (this.$store.state.dataset.dynamic) {
        columns.push({
          label: 'ID',
          field: 'properties.dataPointId',
          tdClass: 'clickable anchor'
        })
      }
      
      // Add coordinate columns (Point: [lng, lat]; Polygon: centroid; LineString/MultiLineString: first point)
      // Use field: 'geometry' so Polygon rows have a truthy value (coordinates.1 is undefined when no holes)
      columns.push({
        label: 'Longitude',
        field: 'geometry',
        formatFn: (value, row) => {
          if (!row.geometry || !row.geometry.coordinates) return ''
          const geom = row.geometry
          if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
            try {
              const c = turf.centroid({ type: 'Feature', properties: {}, geometry: geom })
              return c.geometry.coordinates[0].toFixed(6)
            } catch (e) { return '' }
          }
          if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
            try {
              const lineFeature = { type: 'Feature', properties: {}, geometry: geom }
              const lengthKm = turf.length(lineFeature, { units: 'kilometers' })
              const midpoint = turf.along(lineFeature, lengthKm / 2, { units: 'kilometers' })
              return midpoint.geometry.coordinates[0].toFixed(6)
            } catch (e) { return '' }
          }
          if (geom.type === 'Point' && Array.isArray(geom.coordinates)) return geom.coordinates[0] != null ? Number(geom.coordinates[0]).toFixed(6) : ''
          return ''
        },
        sortFn: (x, y) => {
          const getLng = (g) => {
            if (!g) return 0
            if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
              try {
                const c = turf.centroid({ type: 'Feature', properties: {}, geometry: g })
                return c.geometry.coordinates[0] || 0
              } catch (e) { return 0 }
            }
            if (g.type === 'Point' && Array.isArray(g.coordinates)) return g.coordinates[0] || 0
            if (g.type === 'LineString' || g.type === 'MultiLineString') {
              try {
                const lineFeature = { type: 'Feature', properties: {}, geometry: g }
                const lengthKm = turf.length(lineFeature, { units: 'kilometers' })
                const midpoint = turf.along(lineFeature, lengthKm / 2, { units: 'kilometers' })
                return midpoint.geometry.coordinates[0] || 0
              } catch (e) { return 0 }
            }
            return 0
          }
          return getLng(x) - getLng(y)
        }
      })
      columns.push({
        label: 'Latitude',
        field: 'geometry',
        formatFn: (value, row) => {
          if (!row.geometry || !row.geometry.coordinates) return ''
          const geom = row.geometry
          if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
            try {
              const c = turf.centroid({ type: 'Feature', properties: {}, geometry: geom })
              return c.geometry.coordinates[1].toFixed(6)
            } catch (e) { return '' }
          }
          if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
            try {
              const lineFeature = { type: 'Feature', properties: {}, geometry: geom }
              const lengthKm = turf.length(lineFeature, { units: 'kilometers' })
              const midpoint = turf.along(lineFeature, lengthKm / 2, { units: 'kilometers' })
              return midpoint.geometry.coordinates[1].toFixed(6)
            } catch (e) { return '' }
          }
          if (geom.type === 'Point' && Array.isArray(geom.coordinates)) return geom.coordinates[1] != null ? Number(geom.coordinates[1]).toFixed(6) : ''
          return ''
        },
        sortFn: (x, y) => {
          const getLat = (g) => {
            if (!g) return 0
            if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
              try {
                const c = turf.centroid({ type: 'Feature', properties: {}, geometry: g })
                return c.geometry.coordinates[1] || 0
              } catch (e) { return 0 }
            }
            if (g.type === 'Point' && Array.isArray(g.coordinates)) return g.coordinates[1] || 0
            if (g.type === 'LineString' || g.type === 'MultiLineString') {
              try {
                const lineFeature = { type: 'Feature', properties: {}, geometry: g }
                const lengthKm = turf.length(lineFeature, { units: 'kilometers' })
                const midpoint = turf.along(lineFeature, lengthKm / 2, { units: 'kilometers' })
                return midpoint.geometry.coordinates[1] || 0
              } catch (e) { return 0 }
            }
            return 0
          }
          return getLat(x) - getLat(y)
        }
      })
      const isLineDataset = (this.$store.state.collector && this.$store.state.collector.geometryType === 'line') ||
        (this.$store.state.dataset && this.$store.state.dataset.geometryType === 'line')
      // Area column for polygons, Length column for lines
      columns.push({
        label: isLineDataset ? 'Length' : 'Area',
        field: 'geometry',
        formatFn: (value, row) => {
          if (!row.geometry) return '-'
          try {
            if (isLineDataset && (row.geometry.type === 'LineString' || row.geometry.type === 'MultiLineString')) {
              const lineFeature = { type: 'Feature', properties: {}, geometry: row.geometry }
              const lengthKm = turf.length(lineFeature, { units: 'kilometers' })
              const lengthMeters = lengthKm * 1000
              const ms = this.$store.state.userProfile.measurementSystem
              if (ms === 'Metric') {
                if (lengthMeters >= 1000) return (lengthMeters / 1000).toFixed(3) + ' km'
                return lengthMeters.toFixed(1) + ' m'
              }
              const lengthFeet = lengthMeters * 3.28084
              if (lengthFeet >= 5280) return (lengthFeet / 5280).toFixed(3) + ' mi'
              return lengthFeet.toFixed(1) + ' ft'
            }
            if (row.geometry.type !== 'Polygon' && row.geometry.type !== 'MultiPolygon') return '-'
            const areaSqM = turf.area({ type: 'Feature', properties: {}, geometry: row.geometry })
            const ms = this.$store.state.userProfile.measurementSystem
            if (ms === 'Metric') {
              return (areaSqM / 10000).toFixed(4) + ' ha'
            }
            return (areaSqM * 0.000247105).toFixed(4) + ' ac'
          } catch (e) { return '-' }
        }
      })
      
      // Attribute columns. Dynamic datasets (collector-backed) use the
      // collector's typed field schema. Standard datasets (e.g. CSV uploads)
      // have no collector, so their attributes live only on the GeoJSON
      // feature properties and are enumerated by dataset.headers — use those.
      // (Previously this always read collector.fields, so standard datasets
      // showed NO attribute columns, and a stale collector.fields from a
      // previously-viewed dynamic dataset could leak phantom columns.)
      if (this.$store.state.dataset.dynamic) {
        collectorFields
          .filter(field => field.type !== 'Image') // filter out fields of type 'Image'
          .filter(field => !field.hidden) // filter out fields that are hidden
          .forEach(field => {
            const column = {
              label: field.label,
              field: `properties.${field.label}`
            }
            // If the field type is 'Date and Time', we add a formatFn to handle formatting
            if (field.type === 'Date and Time') {
              const dateTimeFormat = field.includeTime ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd'
              column.formatFn = (value) => value ? format(new Date(value * 1000), dateTimeFormat) : '-'
            }
            // If the field type is 'Number', add numeric sorting
            if (field.type === 'Number') {
              column.sortFn = (x, y) => {
                const xNum = parseFloat(x) || 0
                const yNum = parseFloat(y) || 0
                return xNum - yNum
              }
            }
            columns.push(column)
          })
      } else {
        const headers = this.$store.state.dataset.headers || []
        headers.forEach(header => {
          columns.push({
            label: header,
            field: `properties.${header}`
          })
        })
      }

      // Add updated date column (collector/dynamic datasets only — standard
      // uploads have no per-feature updatedOn, so the column would be blank).
      if (this.$store.state.dataset.dynamic) {
        columns.push({
          label: 'Updated',
          field: 'properties.updatedOn',
          formatFn: (value) => {
            if (!value) return '-'
            let timestamp
            if (value._seconds) {
              // Firestore timestamp object
              timestamp = new Date(value._seconds * 1000)
            } else if (typeof value === 'number') {
              // Direct timestamp value
              timestamp = new Date(value * 1000)
            } else if (value instanceof Date) {
              // Date object
              timestamp = value
            } else {
              return '-'
            }
            return format(timestamp, 'yyyy-MM-dd HH:mm')
          }
        })
      }
      return columns
    },
    selectOptions () {
      return {
        enabled: true,
        selectOnCheckboxOnly: true,
        selectionInfoClass: '',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true,
        selectAllByGroup: false,
        rowKeyField: 'properties.dataPointId'
      }
    },
    // Handle selection across pagination
    allSelectedDatapoints () {
      return this.selectedDatapoints
    }
  },
  methods: {
    async highlightSelectedDatapoint (e) {
      const dataPointIdToToggle = e.features[0].properties.dataPointId

      // Toggle Check on the row
      this.datapointsData.forEach((datapointData, index) => {
        if (datapointData.properties.dataPointId === dataPointIdToToggle) {
          console.log('Toggle Check')
          if (!this.datapointsData[index].vgtSelected) {
            console.log('Selecting')
            this.$set(this.datapointsData[index], 'vgtSelected', true)
            this.selectedDatapointsGeoJson.features.push({
              type: 'Feature',
              properties: e.features[0].properties,
              geometry: e.features[0].geometry
            })
          } else {
            console.log('Deselecting')
            this.$set(this.datapointsData[index], 'vgtSelected', false)
            // Filter out the deselected datapoint
            this.selectedDatapointsGeoJson.features = this.selectedDatapointsGeoJson.features.filter(feature =>
              feature.properties.dataPointId !== dataPointIdToToggle
            )
          }
        }
      })

      if (this.$store.state.mapObject.getLayer('selectDatapointHighlightLayer')) {
        this.$store.state.mapObject.removeLayer('selectDatapointHighlightLayer')
      }
      if (this.$store.state.mapObject.getLayer('selectDatapointHighlightPolygonLayer')) {
        this.$store.state.mapObject.removeLayer('selectDatapointHighlightPolygonLayer')
      }
      if (this.$store.state.mapObject.getLayer('selectDatapointHighlightLineLayer')) {
        this.$store.state.mapObject.removeLayer('selectDatapointHighlightLineLayer')
      }
      if (this.$store.state.mapObject.getSource('selectDatapointHighlightSource')) {
        this.$store.state.mapObject.removeSource('selectDatapointHighlightSource')
      }
      if (this.$store.state.mapObject.getLayer('selectDatapointHighlightLayer')) {
        setTimeout(() => this.addHighlightLayer(), 100)
      } else {
        this.addHighlightLayer()
      }
    },
    selectFromMap () {
      console.log('Select from Map')
      this.$store.commit('setStateProperty', { property: 'showKey', value: false })
      this.$store.commit('setStateProperty', { property: 'datapointSelectionMode', value: true })
      if (this.$store.state.mapObject.getLayer('datapointsLayer')) {
        this.$store.state.mapObject.setLayoutProperty('datapointsLayer', 'visibility', 'none')
      }
      this.$store.commit('shrinkDrawer', 'datasetDetails')
      const map = this.$store.state.mapObject
      const geoJson = this.$store.state.datasetGeoJson
      ;['selectDatapointsLineLayer', 'selectDatapointsPolygonLayer', 'selectDatapointsLayer'].forEach(id => {
        if (map.getLayer(id)) map.removeLayer(id)
      })
      if (map.getSource('selectDatapointsSource')) map.removeSource('selectDatapointsSource')
      map.addSource('selectDatapointsSource', { type: 'geojson', data: geoJson })
      map.addLayer({
        id: 'selectDatapointsLayer',
        type: 'circle',
        source: 'selectDatapointsSource',
        layout: {},
        paint: {
          'circle-radius': this.$store.state.dataset.vizSettings.circleRadius,
          'circle-color': '#6e59c7',
          'circle-opacity': 0.8
        }
      })
      map.addLayer({
        id: 'selectDatapointsPolygonLayer',
        type: 'fill',
        source: 'selectDatapointsSource',
        paint: {
          'fill-color': '#6e59c7',
          'fill-opacity': 0.35,
          'fill-outline-color': '#6e59c7'
        }
      })
      map.addLayer({
        id: 'selectDatapointsLineLayer',
        type: 'line',
        source: 'selectDatapointsSource',
        paint: {
          'line-color': '#6e59c7',
          'line-width': 4,
          'line-opacity': 0.8
        }
      })
      const selectionLayerIds = ['selectDatapointsLayer', 'selectDatapointsPolygonLayer', 'selectDatapointsLineLayer']
      selectionLayerIds.forEach(layerId => {
        map.on('mouseenter', layerId, function () {
          map.getCanvas().style.cursor = 'pointer'
        })
        map.on('mouseleave', layerId, function () {
          map.getCanvas().style.cursor = ''
        })
        map.on('click', layerId, this.highlightSelectedDatapoint)
      })
      if (this.$store.state.mapObject.getLayer('selectDatapointHighlightLayer')) {
        this.$store.state.mapObject.removeLayer('selectDatapointHighlightLayer')
      }
      if (this.$store.state.mapObject.getLayer('selectDatapointHighlightPolygonLayer')) {
        this.$store.state.mapObject.removeLayer('selectDatapointHighlightPolygonLayer')
      }
      if (this.$store.state.mapObject.getLayer('selectDatapointHighlightLineLayer')) {
        this.$store.state.mapObject.removeLayer('selectDatapointHighlightLineLayer')
      }
      if (this.$store.state.mapObject.getSource('selectDatapointHighlightSource')) {
        this.$store.state.mapObject.removeSource('selectDatapointHighlightSource')
      }
      if (this.$store.state.mapObject.getLayer('selectDatapointHighlightLayer')) {
        setTimeout(() => this.addHighlightLayer(), 100)
      } else {
        this.addHighlightLayer()
      }
    },
    handleCellClick (data) {
      if (data.column.label === 'ID' && this.$store.state.dataset.dynamic) {
        const dataPointId = data.row.properties.dataPointId
        fb.collectorsCollection.doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(dataPointId).get().then(doc => {
          const dataPoint = doc.data()
          dataPoint.id = doc.id
          this.$store.commit('setStateProperty', { property: 'dataPoint', value: dataPoint })
          this.$store.commit('hideDrawer', 'datasetDetails')
          setTimeout(() => {
            this.$store.commit('showDrawer', 'dataPointDetails')
            this.$store.dispatch('highlightDatapoint', data.row)
          }, 300)
        }).catch(err => {
          console.log(err.message)
        })
      }
    },
    onSelectionChange ({ selectedRows }) {
      // For pagination, we need to handle selection differently
      // Get all previously selected datapoints that are not on the current page
      const previouslySelected = this.selectedDatapoints.filter(datapoint => 
        !this.datapointsData.some(currentPageDatapoint => 
          currentPageDatapoint.properties.dataPointId === datapoint.properties.dataPointId
        )
      )
      
      // Combine previously selected with current page selections
      const allSelected = [...previouslySelected, ...selectedRows]
      
      this.selectedDatapoints = allSelected
      console.log('Selection Change')
      console.log(this.selectedDatapoints)

      // Update the GeoJSON features for map highlighting
      this.updateSelectedDatapointsGeoJson(allSelected)
    },
    updateSelectedDatapointsGeoJson (selectedDatapoints) {
      // Clear existing features
      this.selectedDatapointsGeoJson.features = []
      
      // Add features for selected datapoints
      selectedDatapoints.forEach(datapoint => {
        this.selectedDatapointsGeoJson.features.push({
          type: 'Feature',
          properties: {
            dataPointId: datapoint.properties.dataPointId
          },
          geometry: datapoint.geometry
        })
      })

      // Update the map highlight layer
      this.updateHighlightLayer()
    },
    updateHighlightLayer () {
      const mapObject = this.$store.state.mapObject
      if (mapObject.getLayer('selectDatapointHighlightLayer')) {
        mapObject.removeLayer('selectDatapointHighlightLayer')
      }
      if (mapObject.getLayer('selectDatapointHighlightPolygonLayer')) {
        mapObject.removeLayer('selectDatapointHighlightPolygonLayer')
      }
      if (mapObject.getLayer('selectDatapointHighlightLineLayer')) {
        mapObject.removeLayer('selectDatapointHighlightLineLayer')
      }
      if (mapObject.getSource('selectDatapointHighlightSource')) {
        mapObject.removeSource('selectDatapointHighlightSource')
      }
      if (
        mapObject.getLayer('selectDatapointHighlightLayer') ||
        mapObject.getLayer('selectDatapointHighlightPolygonLayer') ||
        mapObject.getLayer('selectDatapointHighlightLineLayer')
      ) {
        setTimeout(() => this.addHighlightLayer(), 100)
      } else {
        this.addHighlightLayer()
      }
    },
    addHighlightLayer () {
      const mapObject = this.$store.state.mapObject
      const sourceData = this.selectedDatapointsGeoJson
      mapObject.addSource('selectDatapointHighlightSource', { type: 'geojson', data: sourceData })
      const hasPolygons = sourceData.features && sourceData.features.some(f => f.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'))
      if (hasPolygons) {
        mapObject.addLayer({
          id: 'selectDatapointHighlightPolygonLayer',
          type: 'fill',
          source: 'selectDatapointHighlightSource',
          paint: {
            'fill-color': '#ffc107',
            'fill-opacity': 0.5,
            'fill-outline-color': '#ffc107'
          }
        })
      }
      const hasLines = sourceData.features && sourceData.features.some(f => f.geometry && (f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString'))
      if (hasLines) {
        mapObject.addLayer({
          id: 'selectDatapointHighlightLineLayer',
          type: 'line',
          source: 'selectDatapointHighlightSource',
          paint: {
            'line-color': '#ffc107',
            'line-width': (this.$store.state.dataset.vizSettings && this.$store.state.dataset.vizSettings.lineWidth) ? this.$store.state.dataset.vizSettings.lineWidth + 2 : 4,
            'line-opacity': 0.95
          }
        })
      }
      const hasPoints = sourceData.features && sourceData.features.some(f => f.geometry && f.geometry.type === 'Point')
      if (hasPoints) {
        mapObject.addLayer({
          id: 'selectDatapointHighlightLayer',
          type: 'circle',
          source: 'selectDatapointHighlightSource',
          paint: {
            'circle-radius': (this.$store.state.dataset.vizSettings && this.$store.state.dataset.vizSettings.circleRadius) ? this.$store.state.dataset.vizSettings.circleRadius + 2 : 12,
            'circle-color': '#ffc107',
            'circle-opacity': 0.8
          }
        })
      }
    },
    async deleteSelectedDatapoints (confirmed) {
      this.confirmDelete = false
      if (confirmed) {
        // If no datapoints are selected, do nothing
        if (!this.selectedDatapoints.length) return
        
        // Start progress
        this.processingDatapoints = true
        this.numberOfDatapoints = this.selectedDatapoints.length
        this.datapointProcessing = 0
        const db = fb.db
        
        try {
          for (const datapoint of this.selectedDatapoints) {
            try {
              // Delete datapoint document from Firestore
              await db.collection('collectors').doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(datapoint.properties.dataPointId).delete()
              // Increase processing
              this.datapointProcessing++
            } catch (error) {
              console.error(`Error deleting datapoint with ID: ${datapoint.properties.dataPointId}`, error)
            }
          }
          
          // Wait a moment to ensure all deletions are committed
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // Mark collector for recompilation AFTER all deletions are complete
          console.log('Setting reCompile flag to true for collector (delete):', this.$store.state.dataset.collectorId)
          await db.collection('collectors').doc(this.$store.state.dataset.collectorId).update({
            reCompile: true,
            updatedOn: new Date(),
            updatedBy: this.$store.state.currentUser.uid
          })
          console.log('reCompile flag set successfully (delete)')
          
          // Success - clear selections and show feedback
          this.processingDatapoints = false
          this.clearAllSelections()
          
          // Show success message
          this.$store.commit('setAlert', { 
            active: true, 
            message: `Successfully deleted ${this.numberOfDatapoints} datapoints. Dataset will be recompiled.`, 
            level: 'alert-success',
            timeout: 5000
          })
          
          // Create compilation request with current timestamp to ensure it processes after all updates
          const compileRequest = await fb.compileRequestsCollection.add({
            createdBy: this.$store.state.currentUser.uid,
            createdOn: new Date(),
            collectorId: this.$store.state.dataset.collectorId,
            datasetId: this.$store.state.dataset.id,
            lastUpdated: new Date(), // Use current time to ensure full recompilation
            farmId: this.$store.state.farm.id,
            complete: false
          })
          
          // Wait for compilation to complete
          return new Promise((resolve, reject) => {
            const unsubscribe = compileRequest.onSnapshot(docSnapshot => {
              const data = docSnapshot.data()
              if (data.complete) {
                unsubscribe()
                // Get the updated dataset doc
                fb.datasetsCollection.doc(this.$store.state.dataset.id).get().then(datasetDoc => {
                  const newDataset = datasetDoc.data()
                  newDataset.id = datasetDoc.id
                  // Set new dataset
                  this.$store.commit('setStateProperty', { property: 'dataset', value: newDataset })
                  // Relaunch dataset with updated dataset
                  this.$store.commit('hideDrawer', 'datasetDetails')
                  this.$store.dispatch('removeAllLayers')
                  setTimeout(() => {
                    this.$store.dispatch('launchDataset', { dataset: newDataset })
                  }, 300)
                  resolve()
                }).catch(reject)
              }
            })
          })
          
        } catch (error) {
          // Error handling
          this.processingDatapoints = false
          console.error('Error during deletion:', error)
          this.$store.commit('setAlert', { 
            active: true, 
            message: 'Error deleting datapoints. Please try again.', 
            level: 'alert-danger',
            timeout: 5000
          })
        }
      }
    },
    async bulkUpdateDatapoints (property, newValue) {
      this.numberOfDatapoints = this.selectedDatapoints.length
      this.datapointProcessing = 0
      this.processingDatapoints = true
      
      // This method should implement the logic to update the specified property with the new value for all datapoints in this.selectedDatapoints
      const db = fb.db
      // Convert newValue to number if type is 'Number'
      if (property.type === 'Number') {
        newValue = Number(newValue)
      }

      // Convert newValue to Firestore timestamp if type is 'Date and Time'
      if (property.type === 'Date and Time') {
        if (!(newValue instanceof Date)) {
          console.log('Invalid Date Object', newValue)
          // Convert newValue string to Date object
          newValue = new Date(newValue)
        }
        newValue = firebase.firestore.Timestamp.fromDate(newValue)
      }

      const revisionMessage = `Updated ${property.label} as part of a bulk update.`

      try {
        // First, update all datapoints without setting reCompile flag
        for (const selectedDatapoint of this.selectedDatapoints) {
          this.datapointProcessing++
          // Get current datapoint document from Firestore
          const datapointDoc = await db.collection('collectors').doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(selectedDatapoint.properties.dataPointId).get()
          if (datapointDoc.exists) {
            const datapointData = datapointDoc.data()
            
            // Create a clean copy for revision (without id field to avoid conflicts)
            const revisionData = { ...datapointData }
            delete revisionData.id

            // Create a revision in the 'revisions' subcollection
            await db.collection('collectors').doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(selectedDatapoint.properties.dataPointId).collection('revisions').add(revisionData)

            // Update the datapoint document with the new value
            const updateData = {}
            // Use the machine_name from the property object, which is the correct field name in Firestore
            updateData[property.machine_name] = newValue
            updateData.revisionMessage = revisionMessage
            updateData.updatedOn = new Date()
            updateData.updatedBy = this.$store.state.currentUser.uid
            await db.collection('collectors').doc(this.$store.state.dataset.collectorId).collection('dataPoints').doc(selectedDatapoint.properties.dataPointId).update(updateData)
          }
        }
        
        // Wait a moment to ensure all updates are committed
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Now mark collector for recompilation AFTER all datapoint updates are complete
        console.log('Setting reCompile flag to true for collector:', this.$store.state.dataset.collectorId)
        await db.collection('collectors').doc(this.$store.state.dataset.collectorId).update({
          reCompile: true,
          updatedOn: new Date(),
          updatedBy: this.$store.state.currentUser.uid
        })
        console.log('reCompile flag set successfully')
        
        // Success - reload dataset and clear selections
        this.processingDatapoints = false
        this.clearAllSelections()
        
        // Show success message
        this.$store.commit('setAlert', { 
          active: true, 
          message: `Successfully updated ${this.numberOfDatapoints} datapoints.`, 
          level: 'alert-success',
          timeout: 3000
        })
        
        // Create compilation request with current timestamp to ensure it processes after all updates
        const compileRequest = await fb.compileRequestsCollection.add({
          createdBy: this.$store.state.currentUser.uid,
          createdOn: new Date(),
          collectorId: this.$store.state.dataset.collectorId,
          datasetId: this.$store.state.dataset.id,
          lastUpdated: new Date(), // Use current time to ensure full recompilation
          farmId: this.$store.state.farm.id,
          complete: false
        })
        
        // Wait for compilation to complete
        return new Promise((resolve, reject) => {
          const unsubscribe = compileRequest.onSnapshot(docSnapshot => {
            const data = docSnapshot.data()
            if (data.complete) {
              unsubscribe()
              // Get the updated dataset doc
              fb.datasetsCollection.doc(this.$store.state.dataset.id).get().then(datasetDoc => {
                const newDataset = datasetDoc.data()
                newDataset.id = datasetDoc.id
                // Set new dataset
                this.$store.commit('setStateProperty', { property: 'dataset', value: newDataset })
                // Relaunch dataset with updated dataset
                this.$store.commit('hideDrawer', 'datasetDetails')
                this.$store.dispatch('removeAllLayers')
                setTimeout(() => {
                  this.$store.dispatch('launchDataset', { dataset: newDataset })
                }, 300)
                resolve()
              }).catch(reject)
            }
          })
        })
        
      } catch (error) {
        // Error handling
        this.processingDatapoints = false
        console.error('Error during bulk update:', error)
        this.$store.commit('setAlert', { 
          active: true, 
          message: 'Error updating datapoints. Please try again.', 
          level: 'alert-danger',
          timeout: 5000
        })
      }
    },
    selectAllDatapoints () {
      // Select all datapoints across all pages, not just the current page
      this.selectedDatapoints = this.$store.state.datasetGeoJson.features.map(datapoint => ({ ...datapoint }))
      this.selectedDatapointsGeoJson.features = this.$store.state.datasetGeoJson.features.map(datapoint => ({
        type: 'Feature',
        properties: {
          dataPointId: datapoint.properties.dataPointId
        },
        geometry: datapoint.geometry
      }))
      
      // Sync the table's internal selection state
      this.$nextTick(() => {
        if (this.$refs.datapointsTable) {
          // Mark all visible rows as selected in the table
          this.datapointsData.forEach((datapoint, index) => {
            this.$set(this.datapointsData[index], 'vgtSelected', true)
          })
        }
      })
      
      this.updateHighlightLayer()
    },
    clearAllSelections () {
      this.selectedDatapoints = []
      this.selectedDatapointsGeoJson.features = []
      
      // Sync the table's internal selection state
      this.$nextTick(() => {
        if (this.$refs.datapointsTable) {
          // Mark all visible rows as unselected in the table
          this.datapointsData.forEach((datapoint, index) => {
            this.$set(this.datapointsData[index], 'vgtSelected', false)
          })
        }
      })
      
      this.updateHighlightLayer()
    },
    downloadSelectedCSV () {
      console.log('Download selected datapoints as CSV')
      
      if (!this.selectedDatapoints.length) {
        this.$store.commit('setAlert', { active: true, message: 'No datapoints selected for download.', level: 'alert-warning' })
        return
      }

      const items = []
      const itemHeaders = []

      // Process each selected datapoint
      this.selectedDatapoints.forEach(datapoint => {
        const jsonObject = { ...datapoint.properties }
        
        // Format date fields
        if (jsonObject.createdOn) {
          const date = jsonObject.createdOn * 1000
          jsonObject.createdOn = moment(date).format('YYYY-MM-DD HH:mm:ss')
        }
        if (jsonObject.updatedOn) {
          const date = jsonObject.updatedOn * 1000
          jsonObject.updatedOn = moment(date).format('YYYY-MM-DD HH:mm:ss')
        }

        // Format Date and Time fields for dynamic datasets
        if (this.$store.state.dataset.dynamic) {
          this.$store.state.collector.fields.forEach(field => {
            if (field.type === 'Date and Time' && datapoint.properties[field.label]) {
              const headerDate = datapoint.properties[field.label] * 1000
              if (field.includeTime) {
                jsonObject[field.label] = moment(headerDate).format('YYYY-MM-DD HH:mm:ss')
              } else {
                jsonObject[field.label] = moment(headerDate).format('YYYY-MM-DD')
              }
            }
          })
        }

        if (datapoint.geometry.type === 'Polygon' || datapoint.geometry.type === 'MultiPolygon') {
          try {
            const c = turf.centroid({ type: 'Feature', properties: {}, geometry: datapoint.geometry })
            jsonObject.longitude = c.geometry.coordinates[0]
            jsonObject.latitude = c.geometry.coordinates[1]
          } catch (e) {
            jsonObject.longitude = null
            jsonObject.latitude = null
          }
        } else {
          jsonObject.longitude = datapoint.geometry.coordinates[0]
          jsonObject.latitude = datapoint.geometry.coordinates[1]
        }

        // Track all keys to ensure consistent columns
        const jsonKeys = Object.keys(jsonObject)
        jsonKeys.forEach(key => {
          if (!itemHeaders.includes(key)) {
            itemHeaders.push(key)
          }
        })

        items.push(jsonObject)
      })

      // Handle image fields for dynamic datasets
      if (this.$store.state.dataset.dynamic) {
        let imageField = false
        this.$store.state.collector.fields.forEach(field => {
          if (field.type === 'Image' || field.type === 'Computer Vision') {
            imageField = field
          }
        })
        if (imageField) {
          items.forEach(item => {
            if (item[imageField.label] && item[imageField.label].length > 0) {
              item[imageField.label] = 'Images stored on Every.Farm'
            } else {
              item[imageField.label] = 'No images collected'
            }
          })
        }
      }

      // Ensure all items have all headers (fill missing values with null)
      items.forEach(item => {
        itemHeaders.forEach(header => {
          if (!Object.prototype.hasOwnProperty.call(item, header)) {
            item[header] = null
          }
        })
      })

      // Generate CSV
      const csv = Papa.unparse(items)
      const blob = new Blob([csv], { type: 'text/csv' })
      const urlObj = URL.createObjectURL(blob)
      
      // Create download link
      const a = document.createElement('a')
      a.href = urlObj
      const fileName = `${this.$store.state.dataset.name.replace(/\s+/g, '_')}_selected_${this.selectedDatapoints.length}_datapoints.csv`
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      
      // Clean up
      URL.revokeObjectURL(urlObj)
      
      this.$store.commit('setAlert', { 
        active: true, 
        message: `Successfully downloaded ${this.selectedDatapoints.length} selected datapoints as CSV.`, 
        level: 'alert-success',
        timeout: 3000
      })
    },
    downloadSelectedGeoJSON () {
      if (!this.selectedDatapoints.length) {
        this.$store.commit('setAlert', { active: true, message: 'No datapoints selected for download.', level: 'alert-warning' })
        return
      }
      const fc = {
        type: 'FeatureCollection',
        features: this.selectedDatapoints.map(f => ({ type: f.type, properties: { ...f.properties }, geometry: f.geometry }))
      }
      const json = JSON.stringify(fc, null, 2)
      const blob = new Blob([json], { type: 'application/geo+json' })
      const urlObj = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = urlObj
      a.download = `${this.$store.state.dataset.name.replace(/\s+/g, '_')}_selected_${this.selectedDatapoints.length}_datapoints.geojson`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(urlObj)
      this.$store.commit('setAlert', {
        active: true,
        message: `Successfully downloaded ${this.selectedDatapoints.length} selected datapoints as GeoJSON.`,
        level: 'alert-success',
        timeout: 3000
      })
    },
    exitSelectionMode () {
      this.$store.commit('setStateProperty', { property: 'showKey', value: true })
      this.$store.commit('setStateProperty', { property: 'datapointSelectionMode', value: false })
      const map = this.$store.state.mapObject
      const selectionLayerIds = ['selectDatapointsLineLayer', 'selectDatapointsPolygonLayer', 'selectDatapointsLayer']
      selectionLayerIds.forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId)
        }
      })
      if (map.getSource('selectDatapointsSource')) {
        map.removeSource('selectDatapointsSource')
      }
      selectionLayerIds.forEach(layerId => {
        map.off('click', layerId, this.highlightSelectedDatapoint)
        map.off('mouseenter', layerId)
        map.off('mouseleave', layerId)
      })
    },
    exitSelectionModeAndExpandDrawer () {
      this.exitSelectionMode()
      this.$store.commit('expandDrawer', 'datasetDetails')
    },
    handleKeydown (event) {
      if (event.key === 'Escape' && this.$store.state.datapointSelectionMode) {
        this.exitSelectionMode()
      }
    }
  }
}
</script>

<style scoped>
::v-deep .anchor {
  text-decoration: underline;
}
::v-deep .anchor:hover {
  text-decoration: underline;
  font-weight: bold;
}
</style>
